import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  StoreUserInfoQuery,
  UpdateUserDeliveryInfoMutation,
  UpdateUserDeliveryInfoMutationVariables,
  UserDeliveryInfoFragment,
  UserDeliveryInfoFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  getUserInfoRegisterRedirect,
  setUserInfoRegisterRedirect,
} from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter()
  const { data } = useQuery<StoreUserInfoQuery>(QUERY)
  const [openSMSVerificationModal, setOpenSMSVerificationModal] =
    useState(false)
  const [updateUserDeliveryInfo] = useMutation<
    UpdateUserDeliveryInfoMutation,
    UpdateUserDeliveryInfoMutationVariables
  >(UPDATE_USER_INFORMATION_SETTINGS)

  const [sendVerificationSMS, { loading: sendingVerificationLoading }] =
    useMutation<
      SendVerificationSmsMutation,
      SendVerificationSmsMutationVariables
    >(SendVerificationSms)

  return {
    state: {
      user: data?.user,
      openSMSVerificationModal,
      sendingVerificationLoading,
    },
    actions: {
      onSubmit: async values => {
        // データをフォームに合わせるため、__typenameとfullAddressをaddressから除外する
        const { fullAddress, __typename, ...addressInput } = (data?.user
          ?.address || {
          postalCode: '',
          prefecture: '',
          city: '',
          line1: '',
          line2: '',
        }) as UserDeliveryInfoFragment['address'] & {
          __typename: string
        }

        const userInfoRegisterRedirect = await getUserInfoRegisterRedirect()

        return updateUserDeliveryInfo({
          variables: {
            updateUserDeliveryInfoInputs: {
              fullName: values.fullName,
              phoneNumber: values.phoneNumber,
              address: addressInput,
            },
          },
          async update(cache, { data }) {
            const userDeliveryInfoResponse = data?.updateUserDeliveryInfo
            const existingUser = cache.readQuery<StoreUserInfoQuery>({
              query: QUERY,
            })

            if (existingUser && userDeliveryInfoResponse) {
              cache.writeQuery({
                query: QUERY,
                data: {
                  user: {
                    ...existingUser.user,
                    ...userDeliveryInfoResponse,
                  },
                },
              })
            }
          },
        }).then(async ({ data }) => {
          if (!data?.updateUserDeliveryInfo.isPhoneNumberVerified) {
            setOpenSMSVerificationModal(true)
            return 'verifyPhoneNumber'
          }
          await setUserInfoRegisterRedirect({
            ...userInfoRegisterRedirect,
            phoneNumber: true,
          })

          const redirectPath = userInfoRegisterRedirect.address
            ? routerPages.StoreDeliveryInfo
            : userInfoRegisterRedirect.direction

          if (!redirectPath) {
            router.goBack(routerPages.Setting)
            return
          }
          router.push(redirectPath)
        })
      },
      onCloseSMSVerificationModal: () => {
        setOpenSMSVerificationModal(false)
      },
      async onSendVerificationSMS() {
        await sendVerificationSMS()
        router.push(routerPages.SMSVerification)
      },
    },
  }
})

const QUERY = gql`
  query storeUserInfo {
    user {
      id
      formattedPhoneNumber @client
      ...UserDeliveryInfo
    }
  }
  ${UserDeliveryInfoFragmentDoc}
`

const UPDATE_USER_INFORMATION_SETTINGS = gql`
  mutation updateStoreUserInfo(
    $updateUserDeliveryInfoInputs: UserDeliveryInput!
  ) {
    updateUserDeliveryInfo(inputs: $updateUserDeliveryInfoInputs) {
      ...UserDeliveryInfo
    }
  }
  ${UserDeliveryInfoFragmentDoc}
`
